import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AuthModalVariant} from "../components/auth_modal/AuthModalHeaderVariants";
import {AuthActionType} from "../types/AuthActionType";
import {IAuthModalVariant} from "./auth_modal_slice";

export enum RegistrationFlowStep {
    REGISTRATION = "REGISTRATION",
    PREFERENCES = "PREFERENCES",
    PHONE_COMPLETION = "PHONE_COMPLETION",
    PHONE_VERIFICATION = "PHONE_VERIFICATION",
    LEAD_AFTER_REGISTRATION = "LEAD_AFTER_REGISTRATION",
    MULTILEAD = "MULTILEAD",
    UPDATED_PREFERENCES = "UPDATED_PREFERENCES"
}

export type RegistrationMethod = "email" | "social";
export type SocialMediaType = "google" | "facebook";

export interface IRegistrationFlowPreferences {
    regions: number[];
    price?: number;
    rooms: {lower: number; upper: number};
}

interface IRegistrationFlowState {
    isActive: boolean;
    registrationMethod: RegistrationMethod;
    currentStep: RegistrationFlowStep;
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    preferences: IRegistrationFlowPreferences | null;
    modalVariant: IAuthModalVariant;
    lastAuthAction: AuthActionType | null;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
}

const initialState: IRegistrationFlowState = {
    isActive: false,
    registrationMethod: "email",
    currentStep: RegistrationFlowStep.REGISTRATION,
    isUserLoggedIn: false,
    hasPhoneVerified: false,
    hasCompleteProfile: false,
    userSource: undefined,
    gtmSourceSection: undefined,
    gtmClickText: undefined,
    preferences: null,
    modalVariant: "hidden_prices_variant_b",
    propertyId: undefined,
    offerId: undefined,
    vendorId: undefined,
    lastAuthAction: null,
    titleVariant: AuthModalVariant.HIDDEN_PRICES_VARIANT_B,
    showLoginForm: undefined,
    registrationSuccessCallback: undefined,
    useLeadAfterRegistration: undefined,
    leadSource: undefined,
    leadSourceSection: undefined
};

export interface IStartRegistrationFlowPayload {
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    registrationMethod?: RegistrationMethod;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    modalVariant?: IAuthModalVariant;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
}

export const registrationFlowSlice = createSlice({
    name: "registrationFlow",
    initialState,
    reducers: {
        startRegistrationFlow: (state, action: PayloadAction<IStartRegistrationFlowPayload>) => {
            state.isActive = true;
            state.isUserLoggedIn = action.payload.isUserLoggedIn;
            state.hasCompleteProfile = action.payload.hasCompleteProfile;
            state.hasPhoneVerified = action.payload.hasPhoneVerified;
            state.socialMediaType = action.payload.socialMediaType;
            state.userSource = action.payload.userSource;
            state.gtmSourceSection = action.payload.gtmSourceSection;
            state.gtmClickText = action.payload.gtmClickText;
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
            state.vendorId = action.payload.vendorId;
            state.titleVariant = action.payload.titleVariant;
            state.showLoginForm = action.payload.showLoginForm;
            state.registrationSuccessCallback = action.payload.registrationSuccessCallback;
            state.useLeadAfterRegistration = action.payload.useLeadAfterRegistration;
            state.leadSource = action.payload.leadSource;
            state.leadSourceSection = action.payload.leadSourceSection;
            // Determine starting step based on whether profile completion is needed
            if (!action.payload.isUserLoggedIn) {
                state.currentStep = RegistrationFlowStep.REGISTRATION;
            } else if (!action.payload.hasCompleteProfile) {
                state.currentStep = RegistrationFlowStep.PHONE_COMPLETION;
            } else if (!action.payload.hasPhoneVerified) {
                state.currentStep = RegistrationFlowStep.PHONE_VERIFICATION;
            } else if (action.payload.useLeadAfterRegistration) {
                state.currentStep = RegistrationFlowStep.LEAD_AFTER_REGISTRATION;
            }
        },
        nextStep: (state, action: PayloadAction<{hasCompleteProfile: boolean; hasPhoneVerified: boolean}>) => {
            switch (state.currentStep) {
                case RegistrationFlowStep.REGISTRATION:
                    if (!action.payload.hasCompleteProfile) {
                        state.currentStep = RegistrationFlowStep.PHONE_COMPLETION;
                    } else if (!action.payload.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStep.PHONE_VERIFICATION;
                    } else {
                        state.currentStep = state.useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStep.PHONE_COMPLETION:
                    if (!state.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStep.PHONE_VERIFICATION;
                    } else {
                        state.currentStep = state.useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStep.PHONE_VERIFICATION:
                    state.currentStep = state.useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.LEAD_AFTER_REGISTRATION:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.MULTILEAD:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.PREFERENCES:
                    state.currentStep = RegistrationFlowStep.UPDATED_PREFERENCES;
                    break;
                default:
                    state.isActive = false;
            }
        },
        setStep: (state, action: PayloadAction<{step: RegistrationFlowStep}>) => {
            state.currentStep = action.payload.step;
        },
        savePreferences: (state, action: PayloadAction<IRegistrationFlowPreferences>) => {
            state.preferences = action.payload;
        },
        closeFlow: () => {
            return initialState;
        },
        setLastAuthAction: (state, action: PayloadAction<{authAction: AuthActionType}>) => {
            const {authAction} = action.payload;
            state.lastAuthAction = authAction;
        },
        setRegistrationMethod: (state, action: PayloadAction<{method: RegistrationMethod}>) => {
            state.registrationMethod = action.payload.method;
        },
        setPropertyAndOfferIds: (state, action: PayloadAction<{propertyId?: number; offerId?: number}>) => {
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
        }
    }
});

export const {startRegistrationFlow, nextStep, setStep, savePreferences, closeFlow, setLastAuthAction, setRegistrationMethod, setPropertyAndOfferIds} =
    registrationFlowSlice.actions;
import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AuthModalVariant} from "../components/auth_modal/AuthModalHeaderVariants";
import {AuthActionType} from "../types/AuthActionType";
import {IAuthModalVariant} from "./auth_modal_slice";

export enum RegistrationFlowStep {
    REGISTRATION = "REGISTRATION",
    PREFERENCES = "PREFERENCES",
    PHONE_COMPLETION = "PHONE_COMPLETION",
    PHONE_VERIFICATION = "PHONE_VERIFICATION",
    LEAD_AFTER_REGISTRATION = "LEAD_AFTER_REGISTRATION",
    MULTILEAD = "MULTILEAD",
    UPDATED_PREFERENCES = "UPDATED_PREFERENCES"
}

export type RegistrationMethod = "email" | "social";
export type SocialMediaType = "google" | "facebook";

export interface IRegistrationFlowPreferences {
    regions: number[];
    price?: number;
    rooms: {lower: number; upper: number};
}

interface IRegistrationFlowState {
    isActive: boolean;
    registrationMethod: RegistrationMethod;
    currentStep: RegistrationFlowStep;
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    preferences: IRegistrationFlowPreferences | null;
    modalVariant: IAuthModalVariant;
    lastAuthAction: AuthActionType | null;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
}

const initialState: IRegistrationFlowState = {
    isActive: false,
    registrationMethod: "email",
    currentStep: RegistrationFlowStep.REGISTRATION,
    isUserLoggedIn: false,
    hasPhoneVerified: false,
    hasCompleteProfile: false,
    userSource: undefined,
    gtmSourceSection: undefined,
    gtmClickText: undefined,
    preferences: null,
    modalVariant: "hidden_prices_variant_b",
    propertyId: undefined,
    offerId: undefined,
    vendorId: undefined,
    lastAuthAction: null,
    titleVariant: AuthModalVariant.HIDDEN_PRICES_VARIANT_B,
    showLoginForm: undefined,
    registrationSuccessCallback: undefined,
    useLeadAfterRegistration: undefined,
    leadSource: undefined,
    leadSourceSection: undefined
};

export interface IStartRegistrationFlowPayload {
    isUserLoggedIn?: boolean;
    hasCompleteProfile?: boolean;
    hasPhoneVerified?: boolean;
    registrationMethod?: RegistrationMethod;
    socialMediaType?: SocialMediaType;
    userSource?: UserSource;
    gtmSourceSection?: string;
    gtmClickText?: string;
    modalVariant?: IAuthModalVariant;
    propertyId?: number;
    offerId?: number;
    vendorId?: number;
    titleVariant?: AuthModalVariant;
    showLoginForm?: boolean;
    registrationSuccessCallback?: () => void;
    useLeadAfterRegistration?: boolean;
    leadSource?: ApplicationSource;
    leadSourceSection?: ApplicationSourceSection;
}

export const registrationFlowSlice = createSlice({
    name: "registrationFlow",
    initialState,
    reducers: {
        startRegistrationFlow: (state, action: PayloadAction<IStartRegistrationFlowPayload>) => {
            state.isActive = true;
            state.isUserLoggedIn = action.payload.isUserLoggedIn;
            state.hasCompleteProfile = action.payload.hasCompleteProfile;
            state.hasPhoneVerified = action.payload.hasPhoneVerified;
            state.socialMediaType = action.payload.socialMediaType;
            state.userSource = action.payload.userSource;
            state.gtmSourceSection = action.payload.gtmSourceSection;
            state.gtmClickText = action.payload.gtmClickText;
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
            state.vendorId = action.payload.vendorId;
            state.titleVariant = action.payload.titleVariant;
            state.showLoginForm = action.payload.showLoginForm;
            state.registrationSuccessCallback = action.payload.registrationSuccessCallback;
            state.useLeadAfterRegistration = action.payload.useLeadAfterRegistration;
            state.leadSource = action.payload.leadSource;
            state.leadSourceSection = action.payload.leadSourceSection;
            // Determine starting step based on whether profile completion is needed
            if (!action.payload.isUserLoggedIn) {
                state.currentStep = RegistrationFlowStep.REGISTRATION;
            } else if (!action.payload.hasCompleteProfile) {
                state.currentStep = RegistrationFlowStep.PHONE_COMPLETION;
            } else if (!action.payload.hasPhoneVerified) {
                state.currentStep = RegistrationFlowStep.PHONE_VERIFICATION;
            } else if (action.payload.useLeadAfterRegistration) {
                state.currentStep = RegistrationFlowStep.LEAD_AFTER_REGISTRATION;
            }
        },
        nextStep: (state, action: PayloadAction<{hasCompleteProfile: boolean; hasPhoneVerified: boolean}>) => {
            switch (state.currentStep) {
                case RegistrationFlowStep.REGISTRATION:
                    if (!action.payload.hasCompleteProfile) {
                        state.currentStep = RegistrationFlowStep.PHONE_COMPLETION;
                    } else if (!action.payload.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStep.PHONE_VERIFICATION;
                    } else {
                        state.currentStep = state.useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStep.PHONE_COMPLETION:
                    if (!state.hasPhoneVerified) {
                        state.currentStep = RegistrationFlowStep.PHONE_VERIFICATION;
                    } else {
                        state.currentStep = state.useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;
                    }
                    break;
                case RegistrationFlowStep.PHONE_VERIFICATION:
                    state.currentStep = state.useLeadAfterRegistration ? RegistrationFlowStep.LEAD_AFTER_REGISTRATION : RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.LEAD_AFTER_REGISTRATION:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.MULTILEAD:
                    state.currentStep = RegistrationFlowStep.PREFERENCES;
                    break;
                case RegistrationFlowStep.PREFERENCES:
                    state.currentStep = RegistrationFlowStep.UPDATED_PREFERENCES;
                    break;
                default:
                    state.isActive = false;
            }
        },
        setStep: (state, action: PayloadAction<{step: RegistrationFlowStep}>) => {
            state.currentStep = action.payload.step;
        },
        savePreferences: (state, action: PayloadAction<IRegistrationFlowPreferences>) => {
            state.preferences = action.payload;
        },
        closeFlow: () => {
            return initialState;
        },
        setLastAuthAction: (state, action: PayloadAction<{authAction: AuthActionType}>) => {
            const {authAction} = action.payload;
            state.lastAuthAction = authAction;
        },
        setRegistrationMethod: (state, action: PayloadAction<{method: RegistrationMethod}>) => {
            state.registrationMethod = action.payload.method;
        },
        setPropertyAndOfferIds: (state, action: PayloadAction<{propertyId?: number; offerId?: number}>) => {
            state.propertyId = action.payload.propertyId;
            state.offerId = action.payload.offerId;
        }
    }
});

export const {startRegistrationFlow, nextStep, setStep, savePreferences, closeFlow, setLastAuthAction, setRegistrationMethod, setPropertyAndOfferIds} =
    registrationFlowSlice.actions;
